import { useEffect, useRef } from 'react';

import { GridApi } from '@mui/x-data-grid-premium';

const usePreserveHorizontalScroll = (gridApi: GridApi | null) => {
  const savedScrollLeft = useRef(0);

  useEffect(() => {
    if (!gridApi?.rootElementRef) return;

    const getScroller = () =>
      gridApi.rootElementRef?.current?.querySelector<HTMLElement>('.MuiDataGrid-virtualScroller') ??
      null;

    // Restore position after a remount (a groupBy change remounts the grid at scrollLeft 0).
    // Two rAFs let MUI lay out columns first so the target is scrollable.
    if (savedScrollLeft.current > 0) {
      const target = savedScrollLeft.current;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const scroller = getScroller();
          if (scroller && scroller.scrollLeft !== target) scroller.scrollLeft = target;
        });
      });
    }

    let scroller = getScroller();
    const onScroll = () => {
      if (scroller) savedScrollLeft.current = scroller.scrollLeft;
    };
    scroller?.addEventListener('scroll', onScroll, { passive: true });

    let observer: MutationObserver | undefined;
    const root = gridApi.rootElementRef.current;
    if (!scroller && root) {
      observer = new MutationObserver(() => {
        const found = getScroller();
        if (found) {
          scroller = found;
          found.addEventListener('scroll', onScroll, { passive: true });
          observer?.disconnect();
        }
      });
      observer.observe(root, { childList: true, subtree: true });
    }

    return () => {
      scroller?.removeEventListener('scroll', onScroll);
      observer?.disconnect();
    };
  }, [gridApi]);
};

export default usePreserveHorizontalScroll;
